import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../Home/Navigation';
import VerticalNav from '../Home/VerticalNav';
import { StepIndicatorNames } from '../../types';
import { Button, Loader, Textarea, Checkbox } from '@mantine/core';
import { useNotification } from '../../context/NotificationContext';
import { useCaptionModal } from '../../context/CaptionModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPencilAlt, faCheck, faArrowLeft, faImage } from '@fortawesome/free-solid-svg-icons';
import styles from './TitleDescription.module.css';
import { ThumbnailModal } from './components';
import DependencyModal from '../Modal/DependencyModal';
import { uploadExtractedFrameImage } from '../../services/thumbnailGenerationApi';
import VideoFrameSelectionModal from '../Modal/VideoFrameSelectionModal';
import { getApiUrl } from '../../config/api';
import { useThumbnailModal } from '../../hooks/useThumbnailModal';

// Import the API service functions that handle auth properly
import {
  getTitleData,
  saveTitleSelection,
  updateTitle,
  updateDescription,
  updateHashtags,
  updateTimeline
} from '../../services/titleGenerationApi';

// Types for title generation data
interface TitleData {
  projectId: string;
  generatedTitles: string[];
  selectedTitleIndex: number | null;
  description: string;
  hashtags: string[];
  timeline: TimelineItem[];
  thumbnailUrl?: string;
  generatedVideoUrl?: string;
  captionedVideoUrl?: string;
}

interface TimelineItem {
  time: string;
  description: string;
}

// Helper function to create auth headers
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Helper function for FormData requests (no Content-Type header)
const getAuthHeadersForFormData = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

let originalAudioUrl = '';
let originalVideoUrl = '';

const TitleDescription: React.FC = () => {
  const { t } = useTranslation('titleDescriptionPage');
  const { projectId: pathProjectId } = useParams<{ projectId: string }>();
  const { showNotification } = useNotification();
  const { openCaptionModal } = useCaptionModal();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for project ID from URL params
  const urlParams = new URLSearchParams(location.search);
  const urlProjectId = urlParams.get('project-id');
  const effectiveProjectId = urlProjectId || pathProjectId || '';
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wasRedirected, setWasRedirected] = useState(false);
  const [isEditingProjectTitle, setIsEditingProjectTitle] = useState(false);
  const [projectTitle, setProjectTitle] = useState("Untitled Project");
  const [projectStage, setProjectStage] = useState<string | null>(null);
  const [project, setProject] = useState<any>(null); // Store full project data for VerticalNav
  
  // Title data state - initialize with empty values, will be populated from API
  const [titleData, setTitleData] = useState<TitleData>({
    projectId: effectiveProjectId,
    generatedTitles: [],
    selectedTitleIndex: 0,
    description: "",
    hashtags: [],
    timeline: []
  });
  
  // Thumbnail modal hook (moved after titleData declaration)
  const { modalState: thumbnailModalState, handlers: thumbnailHandlers } = useThumbnailModal(
    effectiveProjectId,
    projectTitle,
    undefined, // no custom video frame handler needed
    titleData.thumbnailUrl, // existing thumbnail
    {
      original: originalVideoUrl,
      generated: titleData.generatedVideoUrl,
      captioned: titleData.captionedVideoUrl
    }
  );
  const [isVideoFrameModalActive, setIsVideoFrameModalActive] = useState(false);
  
  // Video frame selection state
  const [isVideoFrameModalOpen, setIsVideoFrameModalOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  
  // Dependency modal state
  const [dependencyModal, setDependencyModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    primaryButton: { text: '', onClick: null },
    secondaryButton: null
  });

  // Helper function to show dependency modal
  const showDependencyModal = (config: {
    title: string;
    message: string;
    primaryButton: { text: string; onClick: () => void };
    secondaryButton?: { text: string; onClick: () => void } | null;
  }) => {
    setDependencyModal({
      isOpen: true,
      ...config,
      secondaryButton: config.secondaryButton || null
    });
  };

  // Helper function to close dependency modal
  const closeDependencyModal = () => {
    setDependencyModal(prev => ({ ...prev, isOpen: false }));
  };
  
  // Editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(titleData.description);
  const [isEditingTitle, setIsEditingTitle] = useState<number | null>(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [isEditingHashtags, setIsEditingHashtags] = useState(false);
  const [editedHashtags, setEditedHashtags] = useState<string[]>(titleData.hashtags);
  const [isEditingTimeline, setIsEditingTimeline] = useState(false);
  const [editedTimeline, setEditedTimeline] = useState<TimelineItem[]>(titleData.timeline);
  
  // Fetch project details to get title
  useEffect(() => {
    if (!effectiveProjectId) return;
    
    let isMounted = true; // Prevent state updates if component unmounts
    
    const fetchProjectDetails = async () => {
      try {
        const response = await fetch(getApiUrl(`/projects/${effectiveProjectId}`), {
          headers: getAuthHeaders()
        });
        
        if (!isMounted) return; // Component unmounted, don't update state
        
        if (response.ok) {
          const projectData = await response.json();
          console.log('Project data:', projectData);
          
          // Store full project data for VerticalNav
          setProject(projectData);
          
          originalAudioUrl = projectData.originalAudioUrl || '';
          originalVideoUrl = projectData.originalVideoUrl || '';
          
          console.log("-----Original Audio URL:", originalAudioUrl);
          if (projectData && projectData.title) {
            setProjectTitle(projectData.title);
          }
          
          // Capture project stage to check if thumbnail has been used
          if (projectData && projectData.stage) {
            setProjectStage(projectData.stage);
            console.log('Project stage:', projectData.stage);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching project details:", error);
        }
      }
    };
    
    fetchProjectDetails();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [effectiveProjectId]); // Remove location.search dependency to prevent loops

  // Load title data
  useEffect(() => {
    // Validate projectId
    if (!effectiveProjectId) {
      setError(t('titleDescriptionPage.errors.noProjectId'));
      setIsLoading(false);
      return;
    }
    
    let isMounted = true;
    
    const fetchTitleData = async () => {
      try {
        console.log('Fetching title data for project ID:', effectiveProjectId);
        setIsLoading(true);
        setError(null);
        
        // Use the API service function
        const data = await getTitleData(effectiveProjectId);
        
        if (!isMounted) return;
        
        console.log('Received data from API:', data);
        
        // Process the received data
        const newTitleData = {
          projectId: effectiveProjectId,
          generatedTitles: data.generatedTitles || [],
          selectedTitleIndex: data.selectedTitleIndex !== undefined ? data.selectedTitleIndex : 0,
          description: data.description || '',
          hashtags: data.hashtags || [],
          timeline: data.timeline || []
        };
        
        console.log('Setting title data:', newTitleData);
        
        setTitleData(newTitleData);
        setEditedDescription(data.description || '');
        setEditedHashtags(data.hashtags || []);
        setEditedTimeline(data.timeline || []);
        
        setError(null);
        setWasRedirected(false);
        
      } catch (error: any) {
        if (!isMounted) return;
        
        const errorMessage = error?.message || 'An unknown error occurred';
        console.error('Error fetching title data:', error);
        
        if (error?.response?.status === 404) {
          console.log('Project not found (404), using empty state');
          setWasRedirected(true);
          setError(null);
        } else {
          setError(errorMessage);
          showNotification({
            title: t('titleDescriptionPage.errors.title'),
            message: t('titleDescriptionPage.errors.loadFailed', { message: errorMessage }),
            type: 'error'
          });
        }
      } finally {
        if (isMounted) {
          console.log('Setting loading to false');
          setIsLoading(false);
        }
      }
    };
    
    fetchTitleData();
    
    return () => {
      isMounted = false;
    };
  }, [effectiveProjectId, showNotification]);
  
  // Remove the automatic redirect logic as it might interfere with normal operation
  
  // Handle title selection
  const handleSelectTitle = async (index: number) => {
    try {
      setIsLoading(true);
      
      // Skip actual API call if we're in mock mode (wasRedirected)
      if (!wasRedirected) {
        await saveTitleSelection(effectiveProjectId, index);
      }
      
      setTitleData(prev => ({
        ...prev,
        selectedTitleIndex: index
      }));
      
      // showNotification({
      //   title: 'Success',
      //   message: 'Title selection saved',
      //   type: 'success'
      // });
    } catch (error: any) {
      const errorMessage = error?.message || 'An unknown error occurred';
      showNotification({
        title: t('titleDescriptionPage.errors.title'),
        message: t('titleDescriptionPage.errors.saveTitleFailed', { message: errorMessage }),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle title editing
  const handleEditTitle = (index: number) => {
    setIsEditingTitle(index);
    setEditedTitle(titleData.generatedTitles[index]);
  };
  
  const handleSaveEditedTitle = async (index: number) => {
    if (isEditingTitle === null || !editedTitle.trim()) return;
    
    try {
      setIsLoading(true);
      
      // Skip actual API call if we're in mock mode (wasRedirected)
      if (!wasRedirected) {
        await updateTitle(effectiveProjectId, isEditingTitle, editedTitle);
      }
      
      setTitleData(prev => {
        const updatedTitles = [...prev.generatedTitles];
        updatedTitles[index] = editedTitle;
        return {
          ...prev,
          generatedTitles: updatedTitles
        };
      });
      
      setIsEditingTitle(null);
      // showNotification({
      //   title: 'Success',
      //   message: 'Title updated successfully',
      //   type: 'success'
      // });
    } catch (error: any) {
      const errorMessage = error?.message || 'An unknown error occurred';
      showNotification({
        title: t('titleDescriptionPage.errors.title'),
        message: t('titleDescriptionPage.errors.updateTitleFailed', { message: errorMessage }),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle description editing
  const handleEditDescription = () => {
    setIsEditingDescription(true);
    setEditedDescription(titleData.description);
  };
  
  const handleSaveDescription = async () => {
    if (!editedDescription.trim()) return;
    
    try {
      setIsLoading(true);
      
      // Skip actual API call if we're in mock mode (wasRedirected)
      if (!wasRedirected) {
        await updateDescription(effectiveProjectId, editedDescription);
      }
      
      setTitleData(prev => ({
        ...prev,
        description: editedDescription
      }));
      
      setIsEditingDescription(false);
      // showNotification({
      //   title: 'Success',
      //   message: 'Description updated successfully',
      //   type: 'success'
      // });
    } catch (error: any) {
      const errorMessage = error?.message || 'An unknown error occurred';
      showNotification({
        title: t('titleDescriptionPage.errors.title'),
        message: t('titleDescriptionPage.errors.updateDescriptionFailed', { message: errorMessage }),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle hashtags editing
  const handleEditHashtags = () => {
    setIsEditingHashtags(true);
    setEditedHashtags([...titleData.hashtags]);
  };
  
  const handleHashtagChange = (index: number, value: string) => {
    const updated = [...editedHashtags];
    updated[index] = value;
    setEditedHashtags(updated);
  };
  
  const handleAddHashtag = () => {
    setEditedHashtags([...editedHashtags, '#NewTag']);
  };
  
  const handleRemoveHashtag = (index: number) => {
    const updated = editedHashtags.filter((_, i) => i !== index);
    setEditedHashtags(updated);
  };
  
  const handleSaveHashtags = async () => {
    try {
      setIsLoading(true);
      
      // Skip actual API call if we're in mock mode (wasRedirected)
      if (!wasRedirected) {
        await updateHashtags(effectiveProjectId, editedHashtags);
      }
      
      setTitleData(prev => ({
        ...prev,
        hashtags: editedHashtags
      }));
      
      setIsEditingHashtags(false);
      // showNotification({
      //   title: 'Success',
      //   message: 'Hashtags updated successfully',
      //   type: 'success'
      // });
    } catch (error: any) {
      const errorMessage = error?.message || 'An unknown error occurred';
      showNotification({
        title: t('titleDescriptionPage.errors.title'),
        message: t('titleDescriptionPage.errors.updateHashtagsFailed', { message: errorMessage }),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle timeline editing
  const handleEditTimeline = () => {
    setIsEditingTimeline(true);
    setEditedTimeline([...titleData.timeline]);
  };
  
  const handleTimelineItemChange = (index: number, field: 'time' | 'description', value: string) => {
    const updated = [...editedTimeline];
    updated[index] = { ...updated[index], [field]: value };
    setEditedTimeline(updated);
  };
  
  const handleAddTimelineItem = () => {
    setEditedTimeline([...editedTimeline, { time: "0:00", description: "New item" }]);
  };
  
  const handleRemoveTimelineItem = (index: number) => {
    const updated = editedTimeline.filter((_, i) => i !== index);
    setEditedTimeline(updated);
  };
  
  const handleSaveTimeline = async () => {
    try {
      setIsLoading(true);
      
      // Skip actual API call if we're in mock mode (wasRedirected)
      if (!wasRedirected) {
        await updateTimeline(effectiveProjectId, editedTimeline);
      }
      
      setTitleData(prev => ({
        ...prev,
        timeline: editedTimeline
      }));
      
      setIsEditingTimeline(false);
      // showNotification({
      //   title: 'Success',
      //   message: 'Timeline updated successfully',
      //   type: 'success'
      // });
    } catch (error: any) {
      const errorMessage = error?.message || 'An unknown error occurred';
      showNotification({
        title: t('titleDescriptionPage.errors.title'),
        message: t('titleDescriptionPage.errors.updateTimelineFailed', { message: errorMessage }),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle copy to clipboard
  const handleCopyToClipboard = (text: string, itemName: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        showNotification({
          title: t('titleDescriptionPage.success.copied', { item: itemName }),
          message: t('titleDescriptionPage.success.copied', { item: itemName }),
          type: 'success'
        });
      },
      (err) => {
        showNotification({
          title: t('titleDescriptionPage.errors.title'),
          message: t('titleDescriptionPage.errors.copyFailed', { item: itemName, error: err }),
          type: 'error'
        });
      }
    );
  };
  
  // Function to handle navigation step selection
  const handleNavSelected = (key: StepIndicatorNames) => {
    console.log("Nav selection changed to:", key);
    
    // Get the source from URL parameters if available
    const params = new URLSearchParams(location.search);
    const source = params.get('source') || 'title-landing';
    const existingProjectId = params.get('project-id') || effectiveProjectId;
    
    // Handle navigation based on selected step
    switch (key) {
      case StepIndicatorNames.ScriptGeneration:
        // Navigate to script generation for this project
        if (existingProjectId) {
          navigate(`/script-generator/${existingProjectId}`);
        } else {
          navigate('/script-generator');
        }
        break;
        
      case StepIndicatorNames.IdeaCreation:
        // Navigate to prompt generation for this project
        if (existingProjectId) {
          navigate(`/prompt-generator/${existingProjectId}`);
        } else {
          navigate('/prompt-generator');
        }
        break;
        
      case StepIndicatorNames.AudioGeneration:
        // Navigate to standalone audio generation
        navigate('/audio-generation');
        break;
      case StepIndicatorNames.SoundGeneration:
        // Navigate to sound generation for this project
        if (existingProjectId) {
          navigate(`/sound-editor/${existingProjectId}`);
        } else {
          navigate('/sound-generation');
        }
        break;
        
      case StepIndicatorNames.ContentCreation:
        if (existingProjectId) {
          // Navigate to content creation with the existing project ID
          console.log(originalAudioUrl);
          navigate(`/create-content?project-id=${existingProjectId}&audio-url=${originalAudioUrl}&source=title-description`);
        }
        break;
        
      case StepIndicatorNames.CaptionCreation:
        if (existingProjectId) {
          // Debug: Log all possible video URL sources
          console.log('🔍 [TitleDescription] Checking video URLs:');
          console.log('  - project?.generatedVideoUrl:', project?.generatedVideoUrl);
          console.log('  - project?.originalVideoUrl:', project?.originalVideoUrl);
          console.log('  - originalVideoUrl variable:', originalVideoUrl);
          console.log('  - Full project object:', project);
          
          // Check if we have a video URL to generate captions
          const videoUrl = project?.generatedVideoUrl || project?.originalVideoUrl || originalVideoUrl;
          console.log('🔍 [TitleDescription] Final videoUrl determined:', videoUrl);
          
          if (videoUrl) {
            // Video available - open caption generation modal
            console.log('✅ [TitleDescription] Opening caption generation modal for video:', videoUrl);
            openCaptionModal({ 
              videoUrl: videoUrl, 
              projectId: existingProjectId 
            });
          } else {
            // No video available - show dependency modal asking user to upload video
            console.log('📹 [TitleDescription] No video URL found, showing upload video modal');
            showDependencyModal({
              title: t('titleDescriptionPage.dependencyModal.videoRequired.title'),
              message: t('titleDescriptionPage.dependencyModal.videoRequired.message'),
              primaryButton: {
                text: t('titleDescriptionPage.dependencyModal.videoRequired.buttons.goToCaption'),
                onClick: () => {
                  closeDependencyModal();
                  navigate(`/Caption-LandingPage?project-id=${existingProjectId}&source=title-description`);
                }
              },
              secondaryButton: {
                text: t('titleDescriptionPage.dependencyModal.videoRequired.buttons.cancel'),
                onClick: closeDependencyModal
              }
            });
          }
        } else {
          // Fallback to caption landing page without project ID
          navigate('/Caption-LandingPage');
        }
        break;
        
      case StepIndicatorNames.ThumbnailCreation:
        // Always open thumbnail modal, regardless of project stage
        if (existingProjectId) {
          thumbnailHandlers.openModal();
        }
        break;
        
      case StepIndicatorNames.Export:
        if (existingProjectId) {
          // Navigate to export page or trigger export functionality
          navigate(`/create-content?project-id=${existingProjectId}`);
        }
        break;
      // Other cases can be added as needed
    }
  };
  
  // Handle updating the project title
  const handleUpdateProjectTitle = async (newTitle: string) => {
    if (!effectiveProjectId || !newTitle.trim()) return;
    
    try {
      setIsLoading(true);
      
      // Call the API to update the project title with auth headers
      const response = await fetch(getApiUrl(`/projects/${effectiveProjectId}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: newTitle }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project title');
      }
      
      // Update the local state
      setProjectTitle(newTitle);
      setIsEditingProjectTitle(false);
      
      // showNotification({
      //   title: 'Success',
      //   message: 'Project title updated successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error("Error updating project title:", error);
      showNotification({
        title: t('titleDescriptionPage.errors.title'),
        message: t('titleDescriptionPage.errors.updateProjectTitleFailed'),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Thumbnail generation functions - now handled by useThumbnailModal hook

  // Handle opening video frame selection modal
  const handleOpenVideoFrameModal = () => {
    console.log('🎬 [TITLE_PAGE] Opening video frame modal with URL:', originalVideoUrl);
    
    if (originalVideoUrl) {
      // Convert the project video URL to a virtual file for the modal
      const virtualVideoFile = {
        name: `Project Video.mp4`,
        type: 'video/mp4',
        size: 0,
        isProjectVideo: true,
        projectId: effectiveProjectId,
        projectTitle: `Project Video`,
        s3Url: originalVideoUrl,
        lastModified: Date.now(),
        webkitRelativePath: '',
        arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)),
        slice: () => new Blob(),
        stream: () => new ReadableStream(),
        text: () => Promise.resolve('')
      };
      
      console.log('📦 [TITLE_PAGE] Created virtual video file:', virtualVideoFile);
      
      setSelectedVideoFile(virtualVideoFile as any);
      setIsVideoFrameModalOpen(true);
      // Keep the thumbnail modal open in the background
    } else {
      console.error('❌ [TITLE_PAGE] No originalVideoUrl available');
    }
  };

  // Handle frame selection from video extractor
  const handleFrameSelected = async (imageData: string, timestamp: number) => {
    console.log('🎯 [TITLE_PAGE] Frame selected handler called:', { timestamp, imageDataLength: imageData.length });
    
    // Close the video frame modal first
    setIsVideoFrameModalOpen(false);
    setSelectedVideoFile(null);
    
    try {
      // Use hook's setIsGenerating instead of local state
      // setIsGeneratingThumbnail(true); // This will be handled by the hook if needed
      console.log('⏳ [TITLE_PAGE] Starting frame upload process...');
      
      // Upload the extracted frame image using the existing project
      const result = await uploadExtractedFrameImage(imageData, {
        projectId: effectiveProjectId,
        title: `${projectTitle} - Video Frame at ${Math.floor(timestamp / 60)}:${Math.floor(timestamp % 60).toString().padStart(2, '0')}`,
        timestamp: timestamp
      });
      
      console.log('📤 [TITLE_PAGE] Upload result:', result);
      
      if (result) {
        // showNotification({
        //   title: 'Frame Selected',
        //   message: 'Frame extracted successfully! Redirecting to thumbnail editor.',
        //   type: 'success'
        // });
        
        thumbnailHandlers.closeModal();
        
        console.log('🚀 [TITLE_PAGE] Navigating to thumbnail editor...');
        // Navigate to thumbnail editor with the extracted frame
        navigate(`/thumbnail-editor/${effectiveProjectId}?baseImage=${encodeURIComponent(result.s3Url)}`);
      }
    } catch (error) {
      console.error('❌ [TITLE_PAGE] Frame selection error:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to process extracted frame',
        type: 'error'
      });
    } finally {
      // The hook will handle loading state if needed
      // setIsGeneratingThumbnail(false); // Removed - handled by hook
    }
  };

  return (
    <div className={styles.container}>
      <Navigation />
      <VerticalNav 
        handleNavSelected={handleNavSelected} 
        initialStep={StepIndicatorNames.TitleDescTag}
        projectData={project}
      />
      
      <main className={styles.main}>
        <div className={styles.header}>
          <Button 
            className={styles.backButton}
            onClick={() => navigate('/title-landing')}
            title={t('titleDescriptionPage.header.backButton')}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <div className={styles.titleContainer}>
            {isEditingProjectTitle ? (
              <input
                type="text"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                onBlur={() => handleUpdateProjectTitle(projectTitle)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUpdateProjectTitle(projectTitle);
                  if (e.key === 'Escape') setIsEditingProjectTitle(false);
                }}
                className={styles.projectTitleInput}
                autoFocus
              />
            ) : (
              <>
                <h1 className={styles.pageTitle}>{t('titleDescriptionPage.header.title')}</h1>
                <div className={styles.projectNameContainer}>
                  <h2 className={styles.projectName}>{projectTitle}</h2>
                  <button 
                    className={styles.editButton}
                    onClick={() => setIsEditingProjectTitle(true)}
                    title={t('titleDescriptionPage.header.editProjectTitle')}
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Loader size="xl" color="blue" />
            <p>{t('titleDescriptionPage.loading.text')}</p>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{error}</p>
            <Button onClick={() => window.location.reload()}>{t('titleDescriptionPage.errors.retry')}</Button>
          </div>
        ) : (
          <div className={styles.contentContainer}>
            {/* Title Options Section */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>{t('titleDescriptionPage.titleOptions.sectionTitle')}</h2>
                <p className={styles.sectionDescription}>{t('titleDescriptionPage.titleOptions.instructions')}</p>
              </div>
              
              <div className={styles.titlesGrid}>
                {titleData.generatedTitles.map((title, index) => (
                  <div 
                    key={index}
                    className={`${styles.titleCard} ${titleData.selectedTitleIndex === index ? styles.selectedTitle : ''}`}
                  >
                    {isEditingTitle === index ? (
                      <div className={styles.editTitleContainer}>
                        <Textarea
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          minRows={2}
                          autoFocus
                          className={styles.editTitleInput}
                        />
                        <div className={styles.editTitleButtons}>
                          <Button onClick={()=>handleSaveEditedTitle(index)} className={styles.saveButton}>
                            {t('titleDescriptionPage.titleOptions.buttons.save')}
                          </Button>
                          <Button onClick={() => setIsEditingTitle(null)} className={styles.cancelButton}>
                            {t('titleDescriptionPage.titleOptions.buttons.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className={styles.titleText}>{title}</p>
                        <div className={styles.titleActions}>
                          {titleData.selectedTitleIndex === index && (
                            <span className={styles.selectedIndicator}>
                              <FontAwesomeIcon icon={faCheck} />
                            </span>
                          )}
                          <button 
                            className={styles.editButton}
                            onClick={() => handleEditTitle(index)}
                            title={t('titleDescriptionPage.titleOptions.buttons.edit')}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            className={styles.copyButton}
                            onClick={() => handleCopyToClipboard(title, 'Title')}
                            title={t('titleDescriptionPage.titleOptions.buttons.copy')}
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                          <button
                            className={styles.selectButton}
                            onClick={() => handleSelectTitle(index)}
                            disabled={titleData.selectedTitleIndex === index}
                          >
                            {titleData.selectedTitleIndex === index ? t('titleDescriptionPage.titleOptions.buttons.selected') : t('titleDescriptionPage.titleOptions.buttons.select')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </section>
            
            {/* Description Section */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>{t('titleDescriptionPage.description.sectionTitle')}</h2>
                <div className={styles.sectionActions}>
                  <button
                    className={styles.copyButton}
                    onClick={() => handleCopyToClipboard(titleData.description, 'Description')}
                    title={t('titleDescriptionPage.description.buttons.copy')}
                  >
                    <FontAwesomeIcon icon={faCopy} /> {t('titleDescriptionPage.description.buttons.copy')}
                  </button>
                  {!isEditingDescription && (
                    <button
                      className={styles.editButton}
                      onClick={handleEditDescription}
                      title={t('titleDescriptionPage.description.buttons.edit')}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} /> {t('titleDescriptionPage.description.buttons.edit')}
                    </button>
                  )}
                </div>
              </div>
              
              {isEditingDescription ? (
                <div className={styles.editContainer}>
                  <Textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    minRows={6}
                    className={styles.editTextarea}
                  />
                  <div className={styles.editActions}>
                    <Button onClick={handleSaveDescription} className={styles.saveButton}>
                      {t('titleDescriptionPage.description.buttons.save')}
                    </Button>
                    <Button 
                      onClick={() => setIsEditingDescription(false)} 
                      className={styles.cancelButton}
                    >
                      {t('titleDescriptionPage.description.buttons.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={styles.descriptionContent}>
                  <p>{titleData.description}</p>
                </div>
              )}
            </section>
            
            {/* Hashtags Section */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>{t('titleDescriptionPage.hashtags.sectionTitle')}</h2>
                <div className={styles.sectionActions}>
                  <button
                    className={styles.copyButton}
                    onClick={() => handleCopyToClipboard(titleData.hashtags.join(' '), 'Hashtags')}
                    title={t('titleDescriptionPage.hashtags.buttons.copy')}
                  >
                    <FontAwesomeIcon icon={faCopy} /> {t('titleDescriptionPage.hashtags.buttons.copy')}
                  </button>
                  {!isEditingHashtags && (
                    <button
                      className={styles.editButton}
                      onClick={handleEditHashtags}
                      title={t('titleDescriptionPage.hashtags.buttons.edit')}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} /> {t('titleDescriptionPage.hashtags.buttons.edit')}
                    </button>
                  )}
                </div>
              </div>
              
              {isEditingHashtags ? (
                <div className={styles.editContainer}>
                  <div className={styles.hashtagsEdit}>
                    {editedHashtags.map((hashtag, index) => (
                      <div key={index} className={styles.hashtagItem}>
                        <input
                          type="text"
                          value={hashtag}
                          onChange={(e) => handleHashtagChange(index, e.target.value)}
                          className={styles.hashtagInput}
                        />
                        <button
                          onClick={() => handleRemoveHashtag(index)}
                          className={styles.removeButton}
                        >
                          {t('titleDescriptionPage.hashtags.buttons.remove')}
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={handleAddHashtag}
                      className={styles.addButton}
                    >
                      {t('titleDescriptionPage.hashtags.buttons.add')}
                    </button>
                  </div>
                  
                  <div className={styles.editActions}>
                    <Button onClick={handleSaveHashtags} className={styles.saveButton}>
                      {t('titleDescriptionPage.hashtags.buttons.save')}
                    </Button>
                    <Button 
                      onClick={() => setIsEditingHashtags(false)} 
                      className={styles.cancelButton}
                    >
                      {t('titleDescriptionPage.hashtags.buttons.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={styles.hashtagsContainer}>
                  {titleData.hashtags.map((hashtag, index) => (
                    <span key={index} className={styles.hashtag}>
                      {hashtag}
                    </span>
                  ))}
                </div>
              )}
            </section>
            
            {/* Timeline Section */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>{t('titleDescriptionPage.timeline.sectionTitle')}</h2>
                <div className={styles.sectionActions}>
                  <button
                    className={styles.copyButton}
                    onClick={() => {
                      const formatted = titleData.timeline
                        .map(item => `${item.time} - ${item.description}`)
                        .join('\n');
                      handleCopyToClipboard(formatted, 'Timeline');
                    }}
                    title={t('titleDescriptionPage.timeline.buttons.copy')}
                  >
                    <FontAwesomeIcon icon={faCopy} /> {t('titleDescriptionPage.timeline.buttons.copy')}
                  </button>
                  {!isEditingTimeline && (
                    <button
                      className={styles.editButton}
                      onClick={handleEditTimeline}
                      title={t('titleDescriptionPage.timeline.buttons.edit')}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} /> {t('titleDescriptionPage.timeline.buttons.edit')}
                    </button>
                  )}
                </div>
              </div>
              
              {isEditingTimeline ? (
                <div className={styles.editContainer}>
                  <div className={styles.timelineEdit}>
                    {editedTimeline.map((item, index) => (
                      <div key={index} className={styles.timelineItemEdit}>
                        <div className={styles.timeInputContainer}>
                          <label>{t('titleDescriptionPage.timeline.labels.time')}</label>
                          <input
                            type="text"
                            value={item.time}
                            onChange={(e) => handleTimelineItemChange(index, 'time', e.target.value)}
                            className={styles.timeInput}
                          />
                        </div>
                        <div className={styles.descriptionInputContainer}>
                          <label>{t('titleDescriptionPage.timeline.labels.description')}</label>
                          <Textarea
                            value={item.description}
                            onChange={(e) => handleTimelineItemChange(index, 'description', e.target.value)}
                            minRows={2}
                            className={styles.descriptionInput}
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveTimelineItem(index)}
                          className={styles.removeButton}
                        >
                          {t('titleDescriptionPage.timeline.buttons.remove')}
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={handleAddTimelineItem}
                      className={styles.addButton}
                    >
                      {t('titleDescriptionPage.timeline.buttons.add')}
                    </button>
                  </div>
                  
                  <div className={styles.editActions}>
                    <Button onClick={handleSaveTimeline} className={styles.saveButton}>
                      {t('titleDescriptionPage.timeline.buttons.save')}
                    </Button>
                    <Button 
                      onClick={() => setIsEditingTimeline(false)} 
                      className={styles.cancelButton}
                    >
                      {t('titleDescriptionPage.timeline.buttons.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={styles.timelineContainer}>
                  {titleData.timeline.map((item, index) => (
                    <div key={index} className={styles.timelineItem}>
                      <div className={styles.timeMarker}>{item.time}</div>
                      <div className={styles.timelineContent}>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
      
      {/* Thumbnail Continuation Section - shown when project has already used thumbnail app */}
      {projectStage === 'THUMBNAIL_APP' && effectiveProjectId && (
        <div style={{
          padding: '2rem',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderTop: '1px solid rgba(59, 130, 246, 0.3)',
          textAlign: 'center'
        }}>
          <div style={{
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h3 style={{
              color: '#3b82f6',
              marginBottom: '0.5rem',
              fontSize: '1.2rem',
              fontWeight: '600'
            }}>
              {t('titleDescriptionPage.thumbnailContinuation.title')}
            </h3>
            <p style={{
              color: '#e0e0e0',
              marginBottom: '1.5rem',
              fontSize: '1rem'
            }}>
              {t('titleDescriptionPage.thumbnailContinuation.description')}
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Button
                onClick={() => navigate(`/thumbnail-editor/${effectiveProjectId}`)}
                style={{
                  backgroundColor: '#3a86ff',
                  border: 'none',
                  color: 'white',
                  padding: '0.6rem 1rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: 'normal'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a76ef';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a86ff';
                }}
              >
                <FontAwesomeIcon icon={faPencilAlt} style={{ marginRight: '0.5rem' }} />
                {t('titleDescriptionPage.thumbnailContinuation.buttons.continueExisting')}
              </Button>
              <Button
                onClick={() => thumbnailHandlers.openModal()}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #444',
                  color: '#aaa',
                  padding: '0.6rem 1rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#333';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#aaa';
                }}
              >
                <FontAwesomeIcon icon={faImage} style={{ marginRight: '0.5rem' }} />
                {t('titleDescriptionPage.thumbnailContinuation.buttons.createNew')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Thumbnail Generation Modal */}
      <ThumbnailModal
        isOpen={thumbnailModalState.isOpen}
        onClose={thumbnailHandlers.closeModal}
        projectId={effectiveProjectId}
        projectStage={projectStage}
        selectedMethod={thumbnailModalState.selectedMethod}
        onMethodChange={thumbnailHandlers.setMethod}
        description={thumbnailModalState.description}
        onDescriptionChange={thumbnailHandlers.setDescription}
        isGenerating={thumbnailModalState.isGenerating}
        onGenerate={thumbnailHandlers.handleGeneration}
        onImageUpload={thumbnailHandlers.handleImageUpload}
        onFrameSelected={thumbnailHandlers.handleVideoFrameSelection}
        originalVideoUrl={thumbnailModalState.bestVideoUrl}
        availableMethods={thumbnailModalState.availableMethods}
        onExistingThumbnail={thumbnailHandlers.handleExistingThumbnail}
      />

      {/* Video Frame Selection Modal - rendered at top level to avoid conflicts */}
      <VideoFrameSelectionModal
        isOpen={isVideoFrameModalOpen}
        onClose={() => {
          console.log('❌ [TITLE_PAGE] Video frame modal closed without selection');
          setIsVideoFrameModalOpen(false);
          setSelectedVideoFile(null);
        }}
        onFrameSelected={handleFrameSelected}
        videoFile={selectedVideoFile}
        maxFileSize={2 * 1024 * 1024 * 1024} // 2GB
      />

      {/* Dependency Modal for missing video */}
      <DependencyModal
        isOpen={dependencyModal.isOpen}
        title={dependencyModal.title}
        message={dependencyModal.message}
        primaryButton={dependencyModal.primaryButton}
        secondaryButton={dependencyModal.secondaryButton}
        onClose={closeDependencyModal}
      />
    </div>
  );
};

export default TitleDescription;